import React from 'react';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, Eye, Database, Globe, Phone } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const sections = [
    {
      icon: <Database className="text-primary" />,
      title: "Data Collection",
      content: "We collect personal information (owner name, contact details, address) and clinical pet data (medical history, vaccination records, surgical reports) to provide precise veterinary care. This information is stored securely in our clinical management system."
    },
    {
      icon: <Lock className="text-primary" />,
      title: "Confidentiality",
      content: "All medical records and personal details are treated with strict clinical confidentiality. Access is restricted to authorized medical staff and veterinary surgeons involved in your pet's treatment path."
    },
    {
      icon: <Eye className="text-primary" />,
      title: "Third-Party Sharing",
      content: "We only share specific data with trusted third-party clinical partners, such as diagnostic laboratories for bloodwork, advanced imaging centers, or specialized referral surgeons, and only when essential for your pet's diagnosis."
    },
    {
      icon: <Globe className="text-primary" />,
      title: "Digital Privacy",
      content: "Our website uses minimal cookies to enhance performance. We do not sell your personal data to any external marketing agencies. Your contact details are primarily used for appointment reminders and critical health alerts."
    }
  ];

  return (
    <>
      <SEO 
        title="Privacy Policy | Dr. Suresh Pet Care Hospital"
        description="Read our privacy policy to understand how Dr. Suresh Pet Care Hospital & Surgical Center handles your pet and personal information with clinical confidentiality."
        canonical="/privacy"
      />
      <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      className="bg-white min-h-screen pt-[140px] md:pt-[160px] pb-24"
    >
      <div className="section-container">
        {/* Header */}
        <div className="max-w-3xl mb-16 space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full border border-primary/10">
            <ShieldCheck size={14} className="text-primary" />
            <span className="text-[10px] font-black tracking-widest uppercase text-primary">Legal Framework</span>
          </div>
          <h1 className="text-navy font-display font-bold text-5xl md:text-7xl leading-tight">
            Privacy <em className="font-normal text-primary not-italic">Policy.</em>
          </h1>
          <p className="text-muted-foreground text-lg font-medium leading-relaxed">
            At Dr. Suresh Pet Care Hospital, we hold the privacy of our clients and their pets to the highest medical and ethical standards.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-20">
          {sections.map((section, i) => (
            <div key={i} className="p-10 bg-secondary/20 rounded-[3rem] border border-navy/5 hover:border-primary/20 transition-all duration-500">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-8">
                {section.icon}
              </div>
              <h3 className="text-2xl font-bold text-navy mb-4">{section.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed font-medium">
                {section.content}
              </p>
            </div>
          ))}
        </div>

        {/* Contact Note */}
        <div className="p-12 bg-navy rounded-[4rem] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[40%] h-full bg-primary/5 blur-[120px] pointer-events-none" />
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="space-y-4">
              <h4 className="text-2xl font-bold">Questions about your data?</h4>
              <p className="text-white/60 text-sm max-w-md">Our clinical administration team is available to discuss any concerns regarding your pet's medical records or data privacy.</p>
            </div>
            <a href="tel:+919848954921" className="inline-flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-full font-bold text-sm tracking-widest hover:scale-105 transition-transform">
              <Phone size={16} /> CONTACT ADMIN
            </a>
          </div>
        </div>
      </div>
    </motion.div>
    </>
  );
};

export default PrivacyPolicyPage;
